import { RulerParams, RulerResult } from '@/lib/ruler/compute-ruler-ticks'
import type {
  ResizeParams,
  ResizeResult,
  RotationAngleParams,
  RotationAngleResult,
  TransformOriginParams,
  TransformOriginResult,
} from '@/lib/boundaries/bounding-box-boundary'

// Global WASM state
let wasmReady = false
let initPromise: Promise<void> | null = null

// Configuration for WASM module to load
const WASM_MODULE_NAME = 'main' // Change this to load different WASM modules

// Go WASM functions (will be available after initialization)
declare global {
  function computeRulerTicks(jsonParams: string): string
  function computeShapePath(jsonParams: string): string
  function renderShape(jsonParams: string): string
  function computeResize(jsonParams: string): string
  function computeRotationAngle(jsonParams: string): string
  function computeTransformOrigin(jsonParams: string): string
  function computeClikArtResults(jsonParams: string): string
  interface Window {
    Go: new () => {
      importObject: WebAssembly.Imports
      run: (instance: WebAssembly.Instance) => void
    }
  }
}

/**
 * Initialize direct WASM module
 */
async function initWasm(): Promise<void> {
  if (wasmReady) return
  if (initPromise) return initPromise

  initPromise = new Promise((resolve, reject) => {
    try {
      // Determine if we're in development or production
      const isDev = import.meta.env.DEV
      const wasmExecPath = isDev ? '/wasm_exec.js' : '/assets/wasm_exec.js'
      const wasmPath = isDev ? `/${WASM_MODULE_NAME}.wasm` : `/assets/${WASM_MODULE_NAME}.wasm`

      // Load Go WASM runtime
      fetch(wasmExecPath)
        .then((runtimeResponse) => {
          if (!runtimeResponse.ok) {
            throw new Error(`Failed to fetch Go runtime: ${runtimeResponse.status}`)
          }

          return runtimeResponse.text()
        })
        .then((runtimeCode) => {
          // Load WASM binary
          return fetch(wasmPath)
            .then((wasmResponse) => {
              if (!wasmResponse.ok) {
                throw new Error(`Failed to fetch WASM: ${wasmResponse.status}`)
              }

              return wasmResponse.arrayBuffer()
            })
            .then((wasmBytes) => {
              // Load Go runtime in current context
              const script = document.createElement('script')

              script.textContent = runtimeCode
              document.head.appendChild(script)

              // Initialize Go WASM
              const go = new window.Go()
              const wasmModule = new WebAssembly.Module(wasmBytes)
              const wasmInstance = new WebAssembly.Instance(wasmModule, go.importObject)

              // Run Go program
              go.run(wasmInstance)

              wasmReady = true
              resolve()
            })
        })
        .catch(reject)
    } catch (error) {
      reject(error)
    }
  })

  return initPromise
}

/**
 * Check if WASM is ready
 */
export function isDirectWasmReady(): boolean {
  return wasmReady
}

/**
 * Initialize WASM (public API)
 */
export async function initWasmModule(): Promise<void> {
  try {
    await initWasm()
  } catch (error) {
    console.warn('Failed to initialize direct WASM:', error)
    throw error instanceof Error ? error : new Error(String(error))
  }
}

/**
 * Ensure WASM is initialized before use
 */
export async function ensureWasmInitialized(): Promise<void> {
  if (!wasmReady) {
    await initWasmModule()
  }
}

/**
 * WASM computation for combined ruler ticks
 */
export async function computeRulerTicksWasm(params: RulerParams): Promise<RulerResult> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.computeRulerTicks(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { h: parsed.h, v: parsed.v }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

/**
 * WASM computation for shape path
 */
export async function computeShapePathWasm(params: {
  type: string
  width: number
  height: number
}): Promise<{ path: string }> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.computeShapePath(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { path: parsed.path }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

/**
 * WASM computation for shape rendering
 */
export async function renderShapeWasm(params: {
  id: string
  type: string
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  fill?: string
  stroke?: string
  strokeWidth?: number
  opacity?: number
}): Promise<{ success: boolean; svg: string }> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.renderShape(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { success: parsed.success, svg: parsed.svg }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

/**
 * WASM computation for resize calculation
 */
export async function computeResizeWasm(params: ResizeParams): Promise<ResizeResult> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.computeResize(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { x: parsed.x, y: parsed.y, width: parsed.width, height: parsed.height }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

/**
 * WASM computation for rotation angle calculation
 */
export async function computeRotationAngleWasm(
  params: RotationAngleParams
): Promise<RotationAngleResult> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.computeRotationAngle(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { degrees: parsed.degrees }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

/**
 * WASM computation for transform origin calculation
 */
export async function computeTransformOriginWasm(
  params: TransformOriginParams
): Promise<TransformOriginResult> {
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }

    await Promise.race([
      initPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
    ])
  }

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    const jsonParams = JSON.stringify(params)
    const result = globalThis.computeTransformOrigin(jsonParams)

    if (!result) {
      throw new Error('WASM function returned null')
    }

    // Check for error response
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    const parsed = JSON.parse(result)

    return { x: parsed.x, y: parsed.y }
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}

export async function computeClikArtResultsWasm(
  query: string,
  items: Array<{ name: string }>
): Promise<Array<{ name: string }>> {
  // Ensure WASM is ready
  if (!wasmReady) {
    if (!initPromise) {
      initPromise = initWasm()
    }
  }

  await Promise.race([
    initPromise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('WASM init timeout')), 5000)),
  ])

  if (!wasmReady) {
    throw new Error('WASM failed to initialize')
  }

  try {
    // Prepare params, if you use a single JSON param object:
    const jsonParams = JSON.stringify({ query, items })
    // Call Go WASM function registered globally
    const result = globalThis.computeClikArtResults(jsonParams)

    // Check for error response returned as a string JSON
    if (typeof result === 'string' && result.startsWith('{"error":')) {
      const errorObj = JSON.parse(result)

      throw new Error(`WASM computation error: ${errorObj.error}`)
    }

    // Parse JSON result if needed
    const parsed = typeof result === 'string' ? JSON.parse(result) : result

    // Ensure correct format for UI rendering
    if (!parsed || !Array.isArray(parsed.results || parsed)) {
      throw new Error('WASM function returned unexpected result format')
    }
    // Prefer parsed.results if result is wrapped, else use parsed directly

    return parsed.results || parsed
  } catch (error) {
    throw new Error(
      `Direct WASM computation failed: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}
